import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/auth/AuthProvider'
import { track } from '@/lib/analytics'

/**
 * Web access gate (mobile-only billing). Glycemic Snap has no web checkout —
 * Pro is purchased in the mobile app (RevenueCat); this screen says exactly
 * that and offers a refresh for a just-made purchase. When the server could
 * not verify the entitlement at all, it says so and offers a retry instead.
 */
export function Gate() {
  const { t } = useTranslation()
  const { user, access, recheckAccess, signOut, profileMissing } = useAuth()
  const navigate = useNavigate()
  const [refreshing, setRefreshing] = useState(false)

  const viewedRef = useRef(false)
  useEffect(() => {
    if (viewedRef.current) return
    if (profileMissing) {
      viewedRef.current = true
      track('web_no_account_shown')
    } else if (access === 'denied') {
      viewedRef.current = true
      track('paywall_viewed', { source: 'web_gate' })
    }
  }, [profileMissing, access])

  useEffect(() => {
    if (access === 'granted' && !profileMissing) navigate('/diary', { replace: true })
  }, [access, profileMissing, navigate])

  async function onRefresh() {
    setRefreshing(true)
    await recheckAccess()
    setRefreshing(false)
  }

  if (access === 'checking') {
    return (
      <div className="flex min-h-full w-full items-center justify-center bg-bg p-md">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-primary" />
      </div>
    )
  }

  const footer = (
    <div className="mt-lg flex items-center justify-between text-caption text-text-muted">
      <span className="truncate" title={user?.email ?? ''}>{t('webapp.paywall.signedInAs')} {user?.email}</span>
      <button onClick={() => void signOut()} className="text-text-secondary hover:underline">{t('settings.signOut')}</button>
    </div>
  )

  if (profileMissing) {
    return (
      <div className="flex min-h-full w-full items-center justify-center bg-bg p-md">
        <div className="w-full max-w-md animate-fade-in rounded-xxl border border-border-light bg-surface p-xl text-center shadow-lg">
          <img src="/icon-192.png" alt="" className="mx-auto mb-md h-16 w-16 rounded-[18px]" />
          <h1 className="text-h3 font-bold text-text">{t('app.name')}</h1>
          <p className="mt-sm text-body text-text-secondary">{t('webapp.authNoSignup')}</p>
          {footer}
        </div>
      </div>
    )
  }

  const unverified = access === 'unverified'

  return (
    <div className="flex min-h-full w-full items-center justify-center bg-bg p-md">
      <div className="w-full max-w-md animate-fade-in rounded-xxl border border-border-light bg-surface p-xl text-center shadow-lg">
        <img src="/icon-192.png" alt="" className="mx-auto mb-md h-16 w-16 rounded-[18px]" />
        {unverified ? (
          <>
            <h1 className="text-h3 font-bold text-text">{t('app.name')}</h1>
            <p className="mx-auto mt-sm max-w-md text-body text-text-secondary">{t('webapp.paywall.unverified')}</p>
          </>
        ) : (
          <>
            <h1 className="text-h2 font-bold text-text">{t('webapp.paywall.title')}</h1>
            <p className="mx-auto mt-sm max-w-md text-body text-text-secondary">{t('webapp.paywall.subtitleMobile')}</p>
            <p className="mt-md text-body-sm text-text-muted">{t('webapp.paywall.mobileHint')}</p>
          </>
        )}
        <button
          onClick={() => void onRefresh()}
          disabled={refreshing}
          className="mt-lg w-full rounded-lg bg-primary px-md py-md text-button font-semibold text-on-primary transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {refreshing ? t('webapp.paywall.checking') : unverified ? t('common.retry') : t('webapp.paywall.alreadySubscribed')}
        </button>
        {footer}
      </div>
    </div>
  )
}
